import React, { useMemo, useCallback } from "react"
import { Link, useParams } from "react-router-dom"

import { useTranslation } from "providers/i18n"
import { useAppUI } from "providers/ui"

import { getDurationDate } from "utils/date"

import {
  Box,
  CardContent,
  CardMedia,
  Fab,
  List,
  Typography,
  useTheme,
  Divider,
  Stack,
  Button,
} from "@mui/material"

import { usePromotionDetail } from "hooks/promotion/usePromotionDetail"

import { AppNavigation } from "containers/kiosk-v"

import { CalendarIcon, LocationIcon } from "../../components/icons"
import { MotionDiv, showContent } from "components/motions"
import { BackIcon } from "components/icons"
import { LanguageButton } from "components/Language"
import { SearchToggleButton } from "components/domains/search"
import { Head } from "components/Head"
import { InfoListItem } from "components/ListItem"
import { LocationInfo } from "components/domains/features/LocationInfo"
import { QRCode } from "components/QRCode"
import { getIcon } from "utils/icon"
import { FooterAction } from "extensions/one-siam/components/domains/navigation/FooterAction"
import { SearchIcon } from "extensions/one-siam/components/icons"

export const PromotionDetailKioskVPage = ({ Layout = Box }) => {
  const params = useParams()
  const { promotionId } = params
  const { promotion, promotionDetailMobileUrl, navigateToLocation } =
    usePromotionDetail(promotionId)
  const { goBack } = useAppUI()

  const theme = useTheme()

  const calendarIcon = useMemo(
    () => getIcon(theme?.assets?.calendarIcon) || <CalendarIcon />,
    [theme]
  )

  const styleOverrides = theme.components[`VuiDetailCard`]?.styleOverrides || {}
  const {
    root,
    actionButton,
    actionContainer,
    backButton,
    backButtonWrapper,
    buttonContainer,
    button,
    cardContentTextWrapper,
    cardMediaWrapper,
    card,
    cardContent,
    cardHeader,
    cardContentSecondaryText,
    contentWrapper,
    coverBanner,
    detailWrapper,
    divider,
    image,
    infoContainer,
    infoListItem,
    headerWrapper,
    navigationWrapper,
    pageWrapper,
    qrCodeContainer,
    qrCode,
    qrcodePrimaryText,
    qrCodeSecondaryText,
    searchBarContainer,
    toolbarContainer,
    mainMenuContainer,
    footerActionContainer,
  } = styleOverrides

  const { t } = useTranslation()

  const handleClickBack = useCallback(() => {
    goBack()
  }, [goBack])

  const backIcon = useMemo(
    () => getIcon(theme?.assets?.backIcon) || <BackIcon />,
    [theme]
  )

  return (
    <>
      <Head title={`${t(promotion?.name || "")} (${t("Promotion Detail")})`} />
      <Layout>
        <Box sx={root}>
          <MotionDiv
            variants={showContent}
            initial="initial"
            animate="animate"
            sx={pageWrapper}
          >
            {!promotion ? null : (
              <>
                <Box
                  sx={{
                    ...coverBanner,
                    backgroundImage: `url(${promotion.coverImage})`,
                  }}
                />
                <MotionDiv sx={contentWrapper}>
                  <Box sx={card}>
                    <Box sx={cardMediaWrapper}>
                      <CardMedia
                        component="img"
                        sx={image}
                        src={promotion.coverImage}
                      />
                    </Box>
                    <Box sx={detailWrapper}>
                      <Box sx={headerWrapper}>
                        <Typography
                          sx={cardHeader}
                          variant="title"
                          component={"h2"}
                        >
                          {t(promotion.name)}
                        </Typography>
                      </Box>
                      <CardContent sx={cardContent}>
                        <Stack direction="row">
                          <List sx={infoContainer}>
                            {promotion.hasLocation && (
                              <InfoListItem
                                sx={infoListItem}
                                icon={<LocationIcon />}
                                primary={
                                  <LocationInfo
                                    feature={promotion.feature}
                                    component="span"
                                  />
                                }
                              />
                            )}
                            <InfoListItem
                              sx={infoListItem}
                              icon={calendarIcon}
                              primary={getDurationDate(
                                promotion.startDate,
                                promotion.endDate,
                                { monthFormat: "MMMM" }
                              )}
                            />
                          </List>
                        </Stack>
                        <Divider sx={divider} />
                        <Box sx={cardContentTextWrapper}>
                          <Typography
                            variant="body2"
                            sx={cardContentSecondaryText}
                            dangerouslySetInnerHTML={{
                              __html: promotion.descriptionText,
                            }}
                          ></Typography>
                        </Box>
                        <Box sx={buttonContainer}>
                          {promotion.hasLocation && (
                            <Button
                              variant="contained"
                              sx={button}
                              onClick={navigateToLocation}
                            >
                              {t("View Location")}
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Box>
                  </Box>
                  <Box sx={qrCodeContainer} className="target">
                    <QRCode sx={qrCode} url={promotionDetailMobileUrl} />
                    <Stack>
                      <Typography
                        variant="subtitle2"
                        component="p"
                        sx={qrcodePrimaryText}
                      >
                        {t("Scan to view")}
                      </Typography>
                      <Typography
                        variant="caption"
                        component="p"
                        sx={qrCodeSecondaryText}
                      >
                        {t("On mobile")}
                      </Typography>
                    </Stack>
                  </Box>
                </MotionDiv>
              </>
            )}
          </MotionDiv>
        </Box>
        <Box key="app-navigation" sx={navigationWrapper}>
          <Box sx={backButtonWrapper}>
            <Fab variant="rounded" sx={backButton} onClick={handleClickBack}>
              {backIcon}
            </Fab>
          </Box>
          <Box sx={toolbarContainer}>
            <Box sx={actionContainer}>
              <LanguageButton variant="contained" sx={actionButton} />
              {/* //TODO:: Add a wheel chair's mode handler function later */}
              {/*  <Button
                  variant="contained"
                  sx={actionButton}
                  onClick={() => {}}
                  startIcon={<RestroomWheelchairIcon />}
                /> */}
            </Box>
            <Box sx={searchBarContainer}>
              <SearchToggleButton
                icon={SearchIcon}
                placeholder={t("Search shops and more...")}
                LinkComponent={Link}
                to="/search"
              />
            </Box>
          </Box>
          <Box sx={mainMenuContainer}>
            <AppNavigation />
            <FooterAction sx={footerActionContainer} />
          </Box>
        </Box>
      </Layout>
    </>
  )
}
